import React, { useState, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Menu,
  LogOut,
  Video,
  BarChart3,
  Folder,
  Settings,
  ChevronDown,
  Check,
  Target,
} from 'lucide-react';
// import CreditBalance from './organization/CreditBalance';
import { canManageUsers, ROLE_LABELS } from '../utils/permissions';
import { isPublicMvp } from '../lib/featureFlags';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

const roleBadgeClass = (role) => {
  const map = {
    owner: 'bg-amber-50 text-amber-700',
    admin: 'bg-blue-50 text-blue-700',
    editor: 'bg-emerald-50 text-emerald-700',
    brief_editor: 'bg-pink-50 text-pink-700',
    viewer: 'bg-slate-50 text-slate-500',
  };
  return map[role] || 'bg-slate-50 text-slate-500';
};

const Header = () => {
  const { user, logout, organizations, isMultiOrg } = useAuth();
  const { currentOrg, currentRole, setCurrentOrg } = useOrganization();
  const location = useLocation();
  const navigate = useNavigate();
  const { orgSlug } = useParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const basePath = useMemo(() => {
    if (currentOrg) return `/org/${currentOrg.slug}`;
    if (orgSlug) return `/org/${orgSlug}`;
    return '';
  }, [currentOrg, orgSlug]);

  const navItems = useMemo(() => {
    // In public-MVP mode, expose only the paid Creative Studio flow + assets;
    // "My Work" (the seller deal flow) is hidden.
    const items = isPublicMvp()
      ? [
          { path: `${basePath}/creatives`, icon: Video, label: 'New Creative' },
          { path: `${basePath}/assets`, icon: Folder, label: 'Assets' },
        ]
      : [
          { path: `${basePath}/leads`, icon: Target, label: 'My Work' },
          { path: `${basePath}/creatives`, icon: Video, label: 'New Creative' },
          // { path: `${basePath}/campaigns`, icon: BarChart3, label: 'Campaigns' },
          { path: `${basePath}/assets`, icon: Folder, label: 'Assets' },
        ];

    // if (canManageUsers(currentRole)) {
    //   items.push({ path: `${basePath}/members`, icon: Users, label: 'Members' });
    // }

    return items;
  }, [basePath, currentRole]);

  const isActive = (path) => {
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    return location.pathname.includes(`/${lastSegment}`);
  };

  const handleNav = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleSwitchOrg = (org) => {
    setCurrentOrg(org);
    navigate(`/org/${org.slug}/creatives`);
  };

  return (
    <header className="bg-card/95 backdrop-blur-sm border-b border-border sticky top-0 z-50 shadow-[0_1px_3px_0_rgba(0,0,0,0.04)]">
      <div className="flex min-w-0 items-center justify-between gap-3 px-4 md:gap-4 md:px-6 h-16">
        {/* Logo + Org */}
        <div className="flex min-w-0 flex-1 items-center gap-2 md:flex-initial md:shrink-0">
          <a
            href={basePath || '/'}
            className="shrink-0"
            onClick={(e) => { e.preventDefault(); navigate(basePath || '/'); }}
            aria-label="CornerCast — go to home"
          >
            <img
              src="/assets/logos/cornercast-logo-primary.svg"
              alt="CornerCast"
              className="h-7 w-auto max-w-[116px] sm:max-w-none"
            />
          </a>

          {currentOrg && (
            <>
              <span className="text-border text-lg select-none">/</span>
              {isMultiOrg ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex min-w-0 items-center gap-1.5 py-1 text-left text-sm font-semibold text-foreground transition-colors hover:text-coral cursor-pointer">
                      <span className="truncate">{currentOrg.name}</span>
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[220px]">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Switch organization</DropdownMenuLabel>
                    {organizations.map((org) => (
                      <DropdownMenuItem
                        key={org.id}
                        onClick={() => handleSwitchOrg(org)}
                        className="cursor-pointer"
                      >
                        <div className="flex items-center gap-2.5 flex-1">
                          <div className="w-6 h-6 rounded bg-gradient-to-br from-coral to-coral-hover text-white flex items-center justify-center text-[10px] font-semibold shrink-0">
                            {org.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium truncate">{org.name}</span>
                            <span className={cn("text-[10px] font-medium px-1 py-px rounded capitalize w-fit", roleBadgeClass(org.role))}>
                              {ROLE_LABELS[org.role] || org.role}
                            </span>
                          </div>
                        </div>
                        {org.id === currentOrg.id && (
                          <Check className="h-4 w-4 text-teal ml-2 shrink-0" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <span className="max-w-[9rem] truncate text-sm font-semibold text-foreground sm:max-w-none">{currentOrg.name}</span>
              )}
            </>
          )}
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden flex-1 justify-center md:flex">
            <div className="flex gap-1">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors",
                    isActive(item.path)
                      ? "text-foreground border-b-2 border-coral rounded-none"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-lg"
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </button>
              ))}
            </div>
          </nav>

        {/* Right Section */}
        <div className="flex shrink-0 items-center gap-2 md:gap-2.5">
          {/* Demo badge — only when mock mode is active */}
          {import.meta.env.VITE_ENABLE_MOCK_GENERATION === 'true' && (
            <span className="hidden rounded-full bg-coral/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-coral md:inline-flex">
              Demo
            </span>
          )}

          {/* Credit Balance (desktop only) */}
          {/*{currentOrg && (*/}
          {/*  <div className="hidden md:block">*/}
          {/*  <CreditBalance variant="compact" />*/}
          {/*  </div>*/}
          {/*)}*/}

          {/* User Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Avatar className="h-9 w-9 cursor-pointer bg-coral text-white hover:opacity-90 transition-opacity">
                <AvatarFallback className="bg-coral text-white text-sm font-medium">
                  {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-medium">{user?.name || 'User'}</div>
                <div className="text-xs font-normal text-muted-foreground">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {canManageUsers(currentRole) && (
                <DropdownMenuItem onClick={() => navigate(`${basePath}/settings`)}>
                  <Settings className="h-4 w-4 mr-2" />
                  Organization Settings
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[min(88vw,20rem)] max-w-[20rem] gap-0 border-l border-border p-0 sm:max-w-[20rem]">
                <SheetHeader className="border-b border-border px-4 py-4">
                  <SheetTitle className="flex items-center gap-2">
                    <img
                      src="/assets/logos/cornercast-logo-primary.svg"
                      alt="CornerCast"
                      className="h-6"
                    />
                  </SheetTitle>
                </SheetHeader>

                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-6">
                  <div className="space-y-6">
                  {/* Mobile Org Switcher */}
                  {currentOrg && isMultiOrg && (
                    <div className="pb-4 border-b border-border">
                      <p className="text-xs text-muted-foreground mb-2">Organization</p>
                      <div className="space-y-1">
                        {organizations.map((org) => (
                          <button
                            key={org.id}
                            onClick={() => { handleSwitchOrg(org); setMobileMenuOpen(false); }}
                            className={cn(
                              "flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-left transition-colors",
                              org.id === currentOrg.id ? "bg-coral/10" : "hover:bg-muted"
                            )}
                          >
                            <div className="w-6 h-6 rounded bg-gradient-to-br from-coral to-coral-hover text-white flex items-center justify-center text-[10px] font-semibold shrink-0">
                              {org.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-medium flex-1 truncate">{org.name}</span>
                            {org.id === currentOrg.id && <Check className="h-4 w-4 text-teal" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mobile Credit Balance */}
                  {/*{currentOrg && (*/}
                  {/*  <div className="pb-4 border-b border-border">*/}
                  {/*    <CreditBalance variant="expanded" />*/}
                  {/*  </div>*/}
                  {/*)}*/}

                  {/* Mobile Nav */}
                  <nav className="flex flex-col gap-1">
                    {navItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => handleNav(item.path)}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full text-left",
                          isActive(item.path)
                            ? "bg-coral/10 text-coral"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        )}
                      >
                        <item.icon className="h-4 w-4" />
                        {item.label}
                      </button>
                    ))}
                  </nav>

                  <Separator />

                  <div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Signed in as <strong className="text-foreground">{user?.name || user?.email}</strong>
                    </p>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={logout}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </Button>
                  </div>
                </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
